import axios, { AxiosError } from "axios";
import { ErrorToast } from "@core/components/toast/Toast";
import { useUiStore } from "@core/stores/uiStore";
import { COMMON } from "@core/constants/messages";
import { ApiErrorSchema, ApiErrorType } from "@core/model/errorModel";

import type { AxiosRequestConfig } from "axios";

// 커스텀 config 타입
export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  meta?: {
    skipErrorHandler?: boolean;
    [key: string]: any;
  };
}

// 옵션값은 instance.get/post 등 메소드의 인자로 변경 가능
export const apiInstance = axios.create({
  baseURL: "/api-proxy/",
  withCredentials: true,
});

// 응답 인터셉터: 에러 핸들링 공통화
apiInstance.interceptors.response.use(
  (res) => res,
  (err: AxiosError<ApiErrorType>) => {
    // 에러 핸들링 건너뛰기
    const skipHandler = (err.config as CustomAxiosRequestConfig)?.meta?.skipErrorHandler;
    if (skipHandler) return Promise.reject(err);

    const { errorCode, message } = err.response?.data ?? {};

    if (err.name === "AbortError" || err.name === "CanceledError") {
      return Promise.reject(err);
    }

    switch (errorCode) {
      case "UNAUTHENTICATED_USER": // 401
      case "UNKNOWN_API_ERROR": // 500
        {
          // login-info 캐시 초기화 - 각 페이지 별 권한 체크 로직 재실행(AuthGuard)
          const { queryClient } = useUiStore.getState();
          queryClient?.refetchQueries({ queryKey: ["loginInfo"] });
        }
        break;
      default:
        ErrorToast(message ?? COMMON.API_ERROR);
    }

    return Promise.reject(err);
  },
);

export const ErrorHandler = (err: unknown) => {
  if (!isApiError(err)) {
    return Promise.reject(err);
  }

  const { errorCode, message } = err.response?.data ?? {};

  if (err.name === "AbortError" || err.name === "CanceledError") {
    return Promise.reject(err);
  }

  switch (errorCode) {
    case "UNAUTHENTICATED_USER": // 401
    case "UNKNOWN_API_ERROR": // 500
      {
        // login-info 캐시 초기화 - 각 페이지 별 권한 체크 로직 재실행(AuthGuard)
        const { queryClient } = useUiStore.getState();
        queryClient?.invalidateQueries({ queryKey: ["loginInfo"] });
      }
      break;
    default:
      ErrorToast(message ?? COMMON.API_ERROR);
  }

  return Promise.reject(err);
};

export const isApiError = (err: unknown): err is AxiosError<ApiErrorType> => {
  if (!(err instanceof AxiosError)) return false;

  const data = err.response?.data;

  const parsed = ApiErrorSchema.safeParse(data);
  return parsed.success;
};
